import React, { useState, useEffect } from "react";
import axios from "axios";
import Constant from "../Constant";
import Notification from "../Common/Notification";
import EditAppVersionModal from "./EditAppVersionModal";
import { getAccessToken } from "../../hooks/AccessToken";

// New Modal to manage version monitoring cron job
const VersionMonitorModal = ({ open, onClose, showNotification }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form values for new job
  const [durationInDays, setDurationInDays] = useState(2);
  const [intervalInHours, setIntervalInHours] = useState(2);

  // Validation
  const [formError, setFormError] = useState("");

  const token = getAccessToken();

  const fetchStatus = async () => {
    try {
      const response = await axios.get(
        `${Constant.apiBasePath}version-monitor/status?platform=Android`,
        { headers: { token } }
      );
      if (response.data.success) {
        setStatus(response.data.data);
      } else {
        showNotification(response.data.message, "error");
      }
    } catch (error) {
      showNotification("Failed to fetch monitoring status", "error");
      console.error(error);
    }
  };

  const startMonitoring = async () => {
    // Simple form validation
    if (
      !Number.isInteger(Number(durationInDays)) ||
      !Number.isInteger(Number(intervalInHours)) ||
      Number(durationInDays) < 1 ||
      Number(intervalInHours) < 1
    ) {
      setFormError("Enter valid days and interval (positive whole numbers)");
      return;
    }
    setFormError("");
    setLoading(true);
    try {
      const response = await axios.post(
        `${Constant.apiBasePath}version-monitor/start`,
        {
          packageName: "com.storytent_app", // TODO: make dynamic if needed
          platform: "Android",
          durationInDays: Number(durationInDays),
          intervalInHours: Number(intervalInHours),
        },
        { headers: { token } }
      );
      if (response.data.success) {
        showNotification("Version monitoring started successfully");
        fetchStatus();
      } else {
        showNotification(response.data.message, "error");
      }
    } catch (error) {
      showNotification("Failed to start monitoring: " + error.message, "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const stopMonitoring = async () => {
    setLoading(true);
    try {
      const payload = status?.currentJob?.jobId
        ? { jobId: status.currentJob.jobId }
        : { platform: "Android" };
      const response = await axios.post(
        `${Constant.apiBasePath}version-monitor/stop`,
        payload,
        { headers: { token } }
      );
      if (response.data.success) {
        showNotification("Version monitoring stopped");
        setStatus(null);
      } else {
        showNotification(response.data.message, "error");
      }
    } catch (error) {
      showNotification("Failed to stop monitoring: " + error.message, "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchStatus();
      const interval = setInterval(fetchStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0,0,0,0.6)",
      zIndex: 1040,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "8px",
        maxWidth: "500px",
        width: "90%",
        boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
        zIndex: 1050,
        display: "flex",
        flexDirection: "column",
      }}>
        <div style={{
          padding: "1rem 1.5rem",
          borderBottom: "1px solid #dee2e6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <h5 style={{ margin: 0 }}>Version Monitoring</h5>
          <button style={{
            background: "transparent",
            border: "none",
            fontSize: "1.5rem",
            cursor: "pointer",
          }} onClick={onClose}>&times;</button>
        </div>
        <div style={{ padding: "1rem 1.5rem" }}>
          {status?.currentJob ? (
            <>
              <p><strong>Job ID:</strong> {status.currentJob.jobId}</p>
              <p><strong>Status:</strong>
                <span style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  padding: "2px 10px",
                  borderRadius: 6,
                  marginLeft: 6,
                }}>Running</span>
              </p>
              <p><strong>Started At:</strong> {new Date(status.currentJob.startedAt).toLocaleString()}</p>
              <p><strong>Current Version:</strong> {status.currentJob.currentStoredVersion}</p>
              <p><strong>Latest Found:</strong> {status.currentJob.latestFoundVersion || "Checking..."}</p>
              <p><strong>Checks:</strong> {status.currentJob.successfulChecks}/{status.currentJob.totalChecks}</p>
              {status.currentJob.remainingTime && !status.currentJob.remainingTime.expired && (
                <p>
                  <strong>Time Remaining:</strong>{" "}
                  {status.currentJob.remainingTime.days}d {status.currentJob.remainingTime.hours}h
                </p>
              )}
            </>
          ) : (
            <>
              <p>No active monitoring job.</p>
              <div style={{ marginBottom: 12 }}>
                <label>
                  <strong>Duration (days):</strong>
                  <input
                    type="number"
                    min={1}
                    value={durationInDays}
                    style={{
                      marginLeft: 8,
                      padding: "4px 8px",
                      borderRadius: 4,
                      border: "1px solid #ccc",
                      width: "60px",
                    }}
                    onChange={e => setDurationInDays(e.target.value)}
                    disabled={loading}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>
                  <strong>Interval (hours):</strong>
                  <input
                    type="number"
                    min={1}
                    value={intervalInHours}
                    style={{
                      marginLeft: 8,
                      padding: "4px 8px",
                      borderRadius: 4,
                      border: "1px solid #ccc",
                      width: "60px",
                    }}
                    onChange={e => setIntervalInHours(e.target.value)}
                    disabled={loading}
                  />
                </label>
              </div>
              {formError && (
                <div style={{ color: "#dc3545", marginBottom: 8 }}>{formError}</div>
              )}
            </>
          )}
        </div>
        <div style={{
          padding: "1rem 1.5rem",
          borderTop: "1px solid #dee2e6",
          display: "flex",
          justifyContent: "flex-end",
          gap: "0.5rem",
        }}>
          {status?.currentJob ? (
            <button
              style={{
                padding: "0.4rem 1.2rem",
                borderRadius: "4px",
                background: "#dc3545",
                color: "#fff",
                border: "none",
                fontSize: "1rem",
                cursor: "pointer",
                marginRight: "8px",
              }}
              onClick={stopMonitoring}
              disabled={loading}
            >
              {loading ? "Stopping..." : "Stop Monitoring"}
            </button>
          ) : (
            <button
              style={{
                padding: "0.4rem 1.2rem",
                borderRadius: "4px",
                background: "#007bff",
                color: "#fff",
                border: "none",
                fontSize: "1rem",
                cursor: "pointer",
                marginRight: "8px",
              }}
              onClick={startMonitoring}
              disabled={loading}
            >
              {loading ? "Starting..." : "Start Monitoring"}
            </button>
          )}
          <button
            style={{
              padding: "0.4rem 1.2rem",
              borderRadius: "4px",
              background: "#6c757d",
              color: "#fff",
              border: "none",
              fontSize: "1rem",
              cursor: "pointer",
            }}
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


const AppVersions = () => {
  const [appVersions, setAppVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAppVersion, setSelectedAppVersion] = useState(null);
  const [showVersionMonitorModal, setShowVersionMonitorModal] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  // Show notification helper
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Fetch app versions on component mount
  useEffect(() => {
    fetchAppVersions();
  }, []);

  const fetchAppVersions = async () => {
    let token = getAccessToken();
    try {
      setLoading(true);
      const response = await axios.get(
        `${Constant.apiBasePath}appVersion/get-all`,
        { headers: { token: token } }
      );
      setAppVersions(response.data.data);
      setLoading(false);
    } catch (error) {
      showNotification("Failed to fetch app versions", "error");
      console.error("Error fetching app versions:", error);
      setLoading(false);
    }
  };

  // Handle edit app version
  const handleEditClick = (appVersion) => {
    setSelectedAppVersion(appVersion);
    setShowEditModal(true);
  };

  return (
    <div className="content-page">
      <div className="content">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {notification.show && (
                  <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() =>
                      setNotification({ show: false, message: "", type: "" })
                    }
                  />
                )}

                <h4 className="page-title-heading mb-3">App Versions Management</h4>

                {/* Button to open Version Monitor Modal */}
                <button
                  className="btn btn-info mb-4"
                  onClick={() => setShowVersionMonitorModal(true)}
                >
                  Manage Version Monitoring
                </button>

                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    {appVersions.map((version) => (
                      <div key={version._id} className="col-md-6 mb-3">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between">
                              <h5 className="card-title">{version.platform} App Version</h5>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleEditClick(version)}
                              >
                                Edit
                              </button>
                            </div>
                            <div className="mt-2">
                              <p>
                                <strong>Version Number:</strong> {version.versionNo}
                              </p>
                              <p>
                                <strong>Force Update:</strong>{" "}
                                <span
                                  className={`badge text-white ${
                                    version.forceUpdate ? "bg-danger" : "bg-success"
                                  }`}
                                >
                                  {version.forceUpdate ? "Yes" : "No"}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit App Version Modal */}
      {selectedAppVersion && (
        <EditAppVersionModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          appVersion={selectedAppVersion}
          onAppVersionUpdated={fetchAppVersions}
          showNotification={showNotification}
        />
      )}

      {/* Version Monitor Modal */}
      <VersionMonitorModal
        open={showVersionMonitorModal}
        onClose={() => setShowVersionMonitorModal(false)}
        showNotification={showNotification}
      />
    </div>
  );
};

export default AppVersions;
